import { _isNil, _isNotBlank, _notNil } from '@/littledash';
import type { AgentSessionPhase, AgentSessionV1, AgentStreamEventV1 } from '@/model/Agent.model';
import type { FC } from 'react';
import styles from './InVivoPulseResponse.module.scss';

interface InVivoPulseResponseProps {
  phase: AgentSessionPhase;
  streamEvents: Array<AgentStreamEventV1>;
  session: AgentSessionV1 | null;
  error: string | null;
}

const placeholder: Record<AgentSessionPhase, string> = {
  idle: 'Pick a study and ask Pulse a question to begin.',
  creating: 'Starting Pulse session…',
  streaming: 'Pulse is thinking…',
  completed: 'Awaiting report…',
  failed: '',
  cancelled: 'Cancelled.',
};

const stepLabel = (event: AgentStreamEventV1): string => {
  if (event.type === 'execution_started') {
    return 'Execution started';
  }
  if (event.type === 'thinking') {
    return 'Thinking…';
  }
  if (event.type === 'tool_call_start') {
    const step = _notNil(event.step_number) ? `Step ${event.step_number} · ` : '';
    return `${step}${event.tool_name ?? 'tool'} (running)`;
  }
  if (event.type === 'tool_call_end') {
    const step = _notNil(event.step_number) ? `Step ${event.step_number} · ` : '';
    return `${step}${event.tool_name ?? 'tool'}`;
  }
  if (event.type === 'subagent_started') {
    return `Subagent · ${event.subagent_name ?? ''} started`;
  }
  if (event.type === 'subagent_completed') {
    return `Subagent · ${event.subagent_name ?? ''} completed`;
  }
  if (event.type === 'subagent_failed') {
    return `Subagent · ${event.subagent_name ?? ''} failed`;
  }
  return event.type;
};

const extractFinalAnswer = (session: AgentSessionV1 | null): string | null => {
  if (_isNil(session)) {
    return null;
  }
  const displayEvent = session.events.find((event) => event.event_type === 'display_message');
  if (_isNil(displayEvent)) {
    return null;
  }
  const payload = displayEvent.payload as Record<string, unknown> | null;
  const content = payload?.content;
  if (typeof content !== 'string' || !_isNotBlank(content)) {
    return null;
  }
  try {
    const parsed = JSON.parse(content) as { answer?: unknown };
    if (typeof parsed.answer === 'string' && _isNotBlank(parsed.answer)) {
      return parsed.answer;
    }
  } catch {
    return content;
  }
  return content;
};

/**
 * Right-hand panel: while streaming, lists each event the harness emits
 * (thinking, tool calls, subagents). On completion, parses the final
 * display_message payload and renders the markdown answer as preformatted
 * text.
 */
export const InVivoPulseResponse: FC<InVivoPulseResponseProps> = ({ phase, streamEvents, session, error }) => {
  const finalAnswer = extractFinalAnswer(session);
  const showAnswer = phase === 'completed' && _notNil(finalAnswer);
  const showFeed = !showAnswer && streamEvents.length > 0;
  return (
    <div
      className="ui-card pa4 flex flex-column"
      data-test-component="InVivoPulseResponse"
      data-test-element="container"
      data-testid="invivo-pulse-response"
    >
      <h2 className="f4 fw5 ma0 mb3" data-testid="invivo-pulse-response__title">
        Pulse Report
      </h2>
      {phase === 'failed' && (
        <div className="red mb3" data-testid="invivo-pulse-response__error">
          {error ?? 'Pulse failed.'}
        </div>
      )}
      {showAnswer ? (
        <pre className={styles.body} data-testid="invivo-pulse-response__body">
          {finalAnswer}
        </pre>
      ) : showFeed ? (
        <ol className={styles.feed} data-testid="invivo-pulse-response__feed">
          {streamEvents.map((event, index) => (
            <li
              key={`${event.type}-${(event as { step_number?: number }).step_number ?? index}-${index}`}
              className={styles.feedItem}
            >
              {stepLabel(event)}
            </li>
          ))}
        </ol>
      ) : (
        <div className="dark-gray" data-testid="invivo-pulse-response__placeholder">
          {placeholder[phase]}
        </div>
      )}
    </div>
  );
};
